import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import flow from 'lodash/flow';
import isEmpty from 'lodash/isEmpty';
import { seoUrlSport } from '~spa/Utils/SeoUrls';
import { rewriteUrl } from '~shared/utils/uriAliases';
import {
    mapStateToProps,
    mapDispatchToProps,
} from '~shared/components/react/CommonDataListEvents/utils/utils.js';
import {
    getGaAttributeObjectHomepageWidget,
    getGaAttributeObjectWidgetPage,
    getGaAttributeObjectSportPage,
} from '~shared/utils/google-analytics';
import WidgetContainer from '~shared/ui/WidgetContainer';
import WidgetHeader from '~shared/ui/WidgetHeader/WidgetHeader';
import { useTranslation } from 'react-i18next';
import { getSportIdBySlug } from '~shared/utils/sportsbook';
import { ODDS_BOOST_SLUG } from '~shared/utils/constants';
import { showWidget } from '~shared/utils/widgets';
import { withLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad';
import Carousel from '~spa/Components/Carousel';
import MessageButtonCard from '~spa/Components/MessageButtonCard';
import LoadingIndicator from '~spa/Components/LoadingIndicator';
import {
    POST_URL,
    ODDS_BOOST_LOCATIONS,
} from '../constants';
import { generateOddsBoostGtm } from '../utils';
import useOddsBoostEvents from './useOddsBoostEvents';
import oddsBoostList from './oddsBoostList';
import './styles/oddsBoostWidget.scss';

const ODDS_BOOST_WIDGET_CLASS = 'odds-boost-widget';
const ODDS_BOOST_LIST_CLASS = 'odds-boost-list';

const OddsBoostContainer = ({
    events = {},
    commonDataEvents = {},
    source = '',
    lazyLoadHasLoaded = Object.getPrototypeOf(Function),
    isFavouriteSport= false,
    sportSlug = '',
    oddsBoostLocation,
  }) => {
    const [ t ] = useTranslation();
    const isLoading = useOddsBoostEvents(
        POST_URL,
        sportSlug,
        lazyLoadHasLoaded
    );
    const createEventsListSection = (events, commonDataEvents) => {
        const commonEventsKeys = Object.keys(commonDataEvents);
        const widgetEvents = events.filter((event) => commonEventsKeys.includes(String(event.event_id)));

        return oddsBoostList(widgetEvents, commonDataEvents, oddsBoostLocation, source);
    };

    const createHeader = () => {
        const isClickEvent = true;
        const gtmAttribute = generateOddsBoostGtm(oddsBoostLocation, undefined, source);
        const headerUrl = rewriteUrl(seoUrlSport(t('url.odds-boost'), getSportIdBySlug(ODDS_BOOST_SLUG)));

        return (
            <WidgetHeader
                heading={title}
                icon='odds-boost'
                linkText={seeAllText}
                anchorLink={headerUrl}
                gtmTag={`${gtmAttribute}see-all`}
                isClickEvent={isClickEvent}
                isFavouriteSport={isFavouriteSport}
            />
        );
    };

    const getBetLocateAttribute = () => {
        const oddsBoostLabel = 'odds-boost';

        if (source === 'homepage' || source === 'homepage-variant') {

            return getGaAttributeObjectHomepageWidget(oddsBoostLabel, source === 'homepage-variant');
        }
        if (!sportSlug) {

            return getGaAttributeObjectWidgetPage(oddsBoostLabel);
        }

        return getGaAttributeObjectSportPage(sportSlug, oddsBoostLabel);
    };

    const title = t('odds_boost.title');
    const seeAllText = t('betcard.see_all');
    const isWidget = oddsBoostLocation === ODDS_BOOST_LOCATIONS.HOMEPAGE_WIDGET;

    if (!isWidget && isLoading) {
        return (
            <div className={ODDS_BOOST_WIDGET_CLASS}>
                <LoadingIndicator />
            </div>
        );
    }

    if (isEmpty(commonDataEvents) || isEmpty(events) || !showWidget(events, commonDataEvents)) {
        if (!isWidget) {
            return (
                <div {...getBetLocateAttribute() } className={ODDS_BOOST_WIDGET_CLASS}>
                    <div className={ODDS_BOOST_LIST_CLASS}>
                        <MessageButtonCard MessageIconClassName="no-results" message={t('odds_boost.no_odds_boost_avaliable')} />
                    </div>
                </div>
            );
      }

      return null;
    }
    const eventsListSection = createEventsListSection(events, commonDataEvents);

    return (
        <div {...getBetLocateAttribute() } className={ODDS_BOOST_WIDGET_CLASS}>
            {isWidget ? (
                <WidgetContainer>
                    <Carousel
                        jsxHeader={createHeader()}
                        jsxItems={eventsListSection}
                        extraClass={ODDS_BOOST_WIDGET_CLASS}
                    />
                </WidgetContainer>
            ) : (
                <div className={ODDS_BOOST_LIST_CLASS}>
                    {eventsListSection}
                </div>
            )}
      </div>
    );
  };

OddsBoostContainer.propTypes = {
    events: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    commonDataEvents: PropTypes.object,
    source: PropTypes.string,
    lazyLoadHasLoaded: PropTypes.func,
    isFavouriteSport: PropTypes.bool,
    sportSlug: PropTypes.string,
    oddsBoostLocation: PropTypes.number.isRequired,
};

export default flow(
    withLazyLoadContext(),
    connect(mapStateToProps, mapDispatchToProps)
)(OddsBoostContainer);
